import { TokenKind } from './types';

// Common Arabic stopwords / function words
const ARABIC_STOPWORDS = new Set([
  'من', 'في', 'على', 'إلى', 'الي', 'عن', 'مع', 'حتى',
  'هذا', 'هذه', 'هذان', 'هاتان', 'هؤلاء', 'ذلك', 'تلك', 'اولئك', 'أولئك',
  'الذي', 'التي', 'اللذان', 'اللتان', 'الذين', 'اللاتي', 'اللواتي',
  'ما', 'لا', 'لم', 'لن', 'ليس', 'ليست',
  'ان', 'أن', 'إن', 'بان', 'بأن', 'كأن', 'كان', 'كانت', 'يكون', 'تكون',
  'ثم', 'او', 'أو', 'بل', 'لكن', 'قد', 'غير', 'سوى',
  'كل', 'بعض', 'اي', 'أي', 'جميع',
  'بعد', 'قبل', 'حيث', 'حين', 'اذا', 'إذا', 'لو', 'لولا',
  'كيف', 'ماذا', 'متى', 'اين', 'أين', 'لماذا', 'هل',
  'هو', 'هي', 'هم', 'هن', 'هما', 'انا', 'أنا', 'نحن', 'انت', 'أنت', 'انتم', 'أنتم'
]);

/**
 * Calculates weights for a list of tokens based on in-script frequency,
 * function-word classification, length bonus, and token kind.
 */
export function calculateTokenWeights(
  tokens: Array<{ norm: string; kind: TokenKind }>
): number[] {
  const n = tokens.length;
  if (n === 0) return [];

  // Compute term frequencies
  const freqMap = new Map<string, number>();
  for (const t of tokens) {
    if (t.norm) {
      freqMap.set(t.norm, (freqMap.get(t.norm) ?? 0) + 1);
    }
  }

  const weights: number[] = new Array(n);

  for (let i = 0; i < n; i++) {
    const t = tokens[i]!;

    if (t.kind === 'other' || !t.norm) {
      weights[i] = 0.0;
      continue;
    }

    if (t.kind === 'number' || t.kind === 'latin') {
      // Latin and numbers get very low weight and are easily skippable
      weights[i] = 0.15;
      continue;
    }

    // Arabic token weight calculation
    const freq = freqMap.get(t.norm) ?? 1;
    // IDF-like term: higher weight for rarer words in this document
    const idf = Math.log(1 + n / freq);

    // Length bonus: longer Arabic words carry more distinctive phonetic footprint
    const lengthBonus = Math.min(0.4, Math.max(0, (t.norm.length - 2) * 0.06));

    let weight = (1.0 + lengthBonus) * Math.min(2.0, Math.max(0.5, idf));

    // Discount if known stopword
    if (ARABIC_STOPWORDS.has(t.norm)) {
      weight *= 0.35;
    }

    weights[i] = Math.round(weight * 100) / 100;
  }

  return weights;
}
